import { Alert, Autocomplete, TextField, Tooltip } from "@mui/material";
import moment from "moment";

import { Link, useNavigate } from "react-router-dom";

import { useState } from "react";
import { useAuthStore } from "@state/auth";

// Icon Imports
import SettingsIcon from "@mui/icons-material/Settings";

// api imports
import { apiClient } from "@api/index";

// state imports
import { useThemeStore } from "@state/theme";

// toast
import toast from "react-hot-toast";
import { generateDefaultApp } from "@utils/constants";
import { LoadingButton } from "@mui/lab";
import AppStatusChip from "@components/AppStatusChip";
import NavBar from "@components/NavBar";
import { hasAnyPermission } from "@utils/permissions";
import NoAccessScreen from "@components/NoAccessScreen";

export default function App() {
  const user = useAuthStore((state) => state.user);
  const theme = useThemeStore();
  const navigate = useNavigate();

  const canAccessEditor = hasAnyPermission(user.roles, "manage_ott", ["read", "update"]);
  const canAccessSettings = hasAnyPermission(user.roles, "manage_ott", ["create", "delete"]);

  const { mutateAsync: createApp, isPending: createAppIsPending } = apiClient.ott.createApp.useMutation();

  const { data: manifests, status } = apiClient.ott.getClientApps.useQuery({
    clientID: user.clientID,
  });

  const [filterBy, setFilterBy] = useState<string | null>("Newest");

  // If user doesn't have read access, show the no-access screen
  if (!canAccessEditor) {
    return <NoAccessScreen />;
  }

  const filteredApps =
    manifests?.sort((a, b) => {
      if (filterBy === "Newest") {
        return moment(b.manifest.createdAt).diff(moment(a.manifest.createdAt));
      } else if (filterBy === "Oldest") {
        return moment(a.manifest.createdAt).diff(moment(b.manifest.createdAt));
      }
      if (filterBy === "Alphabetical") {
        return a.appName.localeCompare(b.appName);
      }
      if (filterBy === "Last Modified") {
        return moment(b.manifest.updatedAt).diff(moment(a.manifest.updatedAt));
      }
      return 0;
    }) || [];

  const handleNewApp = () => {
    createApp(generateDefaultApp(user.clientID, theme)).then((res) => {
      const newAppID = res[0]?.appID;
      toast.success("New Application Created");

      if (newAppID) {
        navigate(`/editor/${newAppID}`);
      }
    });
  };

  const siteOrigin = window.location.origin;

  return (
    <main className="flex flex-col container mx-auto p-5">
      <NavBar />
      <section className="flex flex-col gap-4 mt-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="font-extrabold"
            >
              All Sites
            </Link>

            {/* <span className="text-gray-500">|</span>

            <Link
              to="/tutorials"
              className=""
            >
              Tutorials
            </Link> */}
          </div>
          <div className="flex items-center gap-6">
            <Autocomplete
              disablePortal
              options={["Newest", "Oldest", "Alphabetical", "Last Modified"]}
              value={filterBy}
              defaultValue="Newest"
              onChange={(event, newValue) => {
                setFilterBy(newValue);
              }}
              getOptionLabel={(option) => {
                return option.toString();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter By"
                  size="small"
                  variant="standard"
                  color="primary"
                  sx={{
                    width: "200px",
                  }}
                />
              )}
            />

            <LoadingButton
              onClick={handleNewApp}
              variant="outlined"
              style={{ borderRadius: "0px" }}
              loading={createAppIsPending}
              disabled={createAppIsPending}
            >
              New Application
            </LoadingButton>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {status === "success" &&
            filteredApps?.map((app) => {
              return (
                <AppCard
                  app={app}
                  siteOrigin={siteOrigin}
                  canAccessEditor={canAccessEditor}
                  canAccessSettings={canAccessSettings}
                  key={app.appID}
                />
              );
            })}
        </div>

        {status === "success" && manifests.length === 0 && (
          <Alert
            severity="info"
            className=""
          >
            You don&apos;t have any applications yet. Click the{" "}
            <Link
              className="hover:underline"
              to="#"
            >
              New Application
            </Link>{" "}
            button above to get started.
          </Alert>
        )}

        {status === "error" && (
          <Alert
            severity="error"
            className=""
          >
            Something went wrong. You may reload the page, or, if the issue persists, contact{" "}
            <Link
              to="mailto:support@invintus.com?subject=Ott%20Error%20Report&body=%5Bissue%20description%2C%20screen%20shot%2C%20etc.%5D%0D%0A"
              className="hover:underline"
            >
              support@invintus.com
            </Link>
          </Alert>
        )}
      </section>
    </main>
  );
}

const AppCard = ({
  app,
  siteOrigin,
  canAccessEditor,
  canAccessSettings,
}: {
  app: any;
  siteOrigin: string;
  canAccessEditor: boolean;
  canAccessSettings: boolean;
}) => {
  const appStatus = app?.manifest?.data?.status;

  return (
    <>
      <div className="flex flex-col gap-2 rounded">
        <div>
          <Tooltip
            title={!canAccessEditor ? "You do not have permission to access the editor" : ""}
            placement="top"
            followCursor
          >
            {canAccessEditor ? (
              <Link
                to={`/editor/${app.appID}`}
                className="block h-40"
              >
                <div className="relative">
                  <div className={`absolute rounded-t inset-0 bg-cover bg-center z-0 h-40 bg-gray-400`} />
                  <div
                    className="select-none opacity-0 h-40 rounded-t hover:opacity-100 absolute inset-0 flex justify-center items-center text-xl text-white
                          transition-all duration-300 ease-in-out bg-black bg-opacity-100 z-10
                        "
                  >
                    Open Editor
                    <div className="ml-2 animate-ping">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8.25 4.5l7.5 7.5-7.5 7.5"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="block h-40 cursor-not-allowed">
                <div className="relative">
                  <div className={`absolute rounded-t inset-0 bg-cover bg-center z-0 h-40 bg-gray-400 opacity-50`} />
                </div>
              </div>
            )}
          </Tooltip>

          <div className="w-full flex items-center">
            <Tooltip
              title={!canAccessEditor ? "You do not have permission to access the editor" : ""}
              placement="top"
              followCursor
            >
              <div className="flex-1">
                {canAccessEditor ? (
                  <Link
                    to={`editor/${app.appID}`}
                    className="block"
                  >
                    <div className="border-b border-l border-r border-gray-300 p-2">
                      <div className="flex items-center justify-center rounded-md bg-gray-200 text-gray-600 hover:text-blue-500 px-2 py-0.5">
                        <p className="text-sm ml-2 select-none">Open Editor</p>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="border-b border-l border-r border-gray-300 p-2">
                    <div className="flex items-center justify-center rounded-md bg-gray-300 text-gray-400 cursor-not-allowed px-2 py-0.5">
                      <p className="text-sm ml-2 select-none">Open Editor</p>
                    </div>
                  </div>
                )}
              </div>
            </Tooltip>

            <div className="flex items-center justify-center border-b border-r rounded-br border-gray-300 p-2">
              <Tooltip
                title={!canAccessSettings ? "You do not have permission to access settings" : "Settings"}
                arrow
              >
                {canAccessSettings ? (
                  <Link
                    to={`/app/settings/${app.appID}`}
                    className="flex items-center justify-center px-2 py-0.5"
                  >
                    <SettingsIcon className="w-5 h-5 text-gray-400 hover:text-gray-800" />
                  </Link>
                ) : (
                  <div className="flex items-center justify-center px-2 py-0.5 cursor-not-allowed">
                    <SettingsIcon className="w-5 h-5 text-gray-300" />
                  </div>
                )}
              </Tooltip>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <div className="font-bold text-sm">{app.appName}</div>

            <AppStatusChip status={appStatus} />
          </div>
          <Link
            className="text-xs text-gray-500 hover:underline"
            to={`${siteOrigin}/preview/${app.appID}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {siteOrigin}/preview/{app.appID}
          </Link>
        </div>
      </div>
    </>
  );
};
